import { useContext, useEffect, useState } from "react";
import styles from "./styles/PosTicketResult.module.css";
import OutputData from "../../../components/ui/output_data/OutputData";
import OutputLocation from "../../../components/ui/output_location/OutputLocation";
import OutputWindowInfo from "../../../components/ui/output_window_info/OutputWindowInfo";
import {
    formatDateToLocalDate,
    calculateValues,
    getInstalmentColor,
    capitalizeFirstLetter,
} from "../../../utils/utils";
import { useTranslation } from "react-i18next";
import PosPhotoPanel from "./PosPhotoPanel";
import POSDataContext from "../../../context/POSDataContext";
import OutputDataDetails from "../../../components/output_data_details/OutputDataDetails";
import OutputWindowSettlement from "../../../components/ui/output_window_Settlement/OutputWindowSettlement";
import GetTicketInfoWindowContent from "./get_ticket_info_window_content_pos/GetTicketInfoWindowContentPos";
import OutputLegend from "../../../components/ui/output_legend/OutputLegend";
import OutputWindowAmount from "../../../components/ui/output_window_amount/OutputWindowAmount";
import { ReactComponent as IconTicket } from "../../../assets/ticket.svg";
import { ReactComponent as IconCalendarDay } from "../../../assets/calendar-day.svg";
import { ReactComponent as IconCalendar } from "../../../assets/calendar.svg";
import { ReactComponent as IconHistory } from "../../../assets/history.svg";
import { ReactComponent as IconCoins } from "../../../assets/coins.svg";
import { ReactComponent as IconGavel } from "../../../assets/gavel.svg";
import OutputScofflaw from "../../../components/ui/output_scofflow/OutputScofflaw";

/**
 * Ticket info component
 */
const PosTicketInfo = ({
    color, // {String} - The current theme color
    currency, // {String} - The currency symbol used
    ticket, // {Object} - The ticket data object

    currenInstalmentPos,
}) => {
    const { t } = useTranslation();

    const { ticketTypePos } = useContext(POSDataContext);

    /**
     * Filter the transactionItems array and against filterString array and retun the items array
     * after excluding the ones in the filterString
     * @param {Array} items - The transaction items (Array of Objects)
     * @param {Array} filterStrings - The names of the transactions to exclude
     * @returns {Array} - The transaction items after filtering
     */
    const filterTransactionItems = (items, filterStrings) => {
        return (
            items?.length &&
            items.filter((item) => !filterStrings.includes(item?.txt))
        );
    };

    // Get  penalty, total values using calculateValues function from utils.
    // The order of the inputs is IMPORTANT!
    const { penalty, total, tax } = calculateValues(
        ticket?.paymentBalance,
        ticket?.amount,
        ticket?.transactionItems,
    );

    const [resetShow, setResetShow] = useState(false);

    useEffect(() => {
        setResetShow((prev) => !prev);
    }, [ticket]);

    return (
        <section className={`${styles.main_info_container} v-flex-container`}>
            <h2 className={`${styles.main_info_container_header}`}>
                {ticketTypePos === 0
                    ? t(`ticket details`)
                    : t(`settlement details`)}
            </h2>
            {ticketTypePos === 0 ? (
                <div className={`${styles.info_container} v-flex-container`}>
                    {ticket?.scofflawCaseNumber ? (
                        <div
                            className={`${styles.output_info_container} ${styles.output_scofflaw_container}`}
                        >
                            <OutputScofflaw
                                header={`${t("scofflaw")}: ${ticket?.scofflawCaseNumber}`}
                                body={capitalizeFirstLetter(
                                    t("this vehicle has unpaid violations"),
                                )}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    <div className={`${styles.info_container} flex-container`}>
                        <div
                            className={`${styles.info_data_container} v-flex-container`}
                        >
                            <OutputData
                                label={t(`ticket number`)}
                                data={ticket?.ticketNo}
                                icon={IconTicket}
                                color={color}
                            />
                            <OutputData
                                label={t(`ticket date`)}
                                data={formatDateToLocalDate(
                                    ticket?.from_DateTime,
                                )}
                                icon={IconCalendarDay}
                                color={color}
                            />
                            <OutputData
                                label={t(`issue date`)}
                                data={formatDateToLocalDate(
                                    ticket?.to_DateTime,
                                )}
                                icon={IconCalendar}
                                color={color}
                            />
                            <OutputLocation
                                label={t(`location`)}
                                data={ticket?.street}
                                color={{ hover: color, main: "#F5F5F5" }}
                                location={ticket?.location}
                                ticketNumber={ticket?.ticketNo}
                            />
                        </div>

                        <div
                            className={`${styles.info_windows_container}  ${styles.info_container}  v-flex-container`}
                        >
                            {/* Amount window */}
                            <div
                                className={`${styles.output_amount_container}`}
                            >
                                <OutputWindowAmount
                                    amount={parseInt(ticket?.amount)}
                                    penalty={penalty}
                                    total={total}
                                    tax={tax}
                                    currency={currency}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={`${styles.output_info_container}`}>
                        <OutputWindowInfo
                            exKey={ticket?.ticketNo}
                            header={t(`violation`)}
                            body={ticket?.offences}
                        />
                    </div>
                    <div className={`${styles.output_info_container}`}>
                        <OutputDataDetails
                            reset={resetShow}
                            label={t(`charges`)}
                            ticketId={ticket?.ticketID}
                            data={filterTransactionItems(
                                ticket?.transactionItems,
                                ["Offence amount"],
                            )}
                            notAvialableMessage={t(`no charges`)}
                            icon={IconHistory}
                            placeHolder={t("view charges")}
                        />
                    </div>

                    {ticket?.photos?.length ? (
                        <div
                            className={`${styles.info_windows_container} ${styles.info_carousel} v-flex-container`}
                        >
                            <PosPhotoPanel ticket={ticket} />
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            ) : (
                // ----------------------------------------------------------------------------------------------------- Settlement
                <>
                    <div className={`${styles.info_container} flex-container`}>
                        <div
                            className={`${styles.info_data_container} v-flex-container`}
                        >
                            <OutputData
                                label={t(`case number`)}
                                data={ticket?.caseNo}
                                icon={IconGavel}
                            />

                            <OutputData
                                label={t(`paid instalments`)}
                                data={`${t("paid")} ${
                                    ticket?.paidInstallments
                                } ${t(`out of`)} ${ticket?.totalInstallments} `}
                                icon={IconCoins}
                            />
                            <OutputDataDetails
                                label={t(`tickets`)}
                                data={ticket?.includedTicketNumbers}
                                notAvialableMessage={t(`no tickets`)}
                                icon={IconTicket}
                                placeHolder={t("view tickets")}
                            />
                        </div>

                        <div
                            className={`${styles.info_windows_container} v-flex-container`}
                        >
                            {/* Amount window */}
                            <div
                                className={`${styles.output_amount_container}`}
                            >
                                <OutputWindowSettlement
                                    fullAmount={ticket?.totalAmount}
                                    remain={ticket?.remainingAmount}
                                    currency={currency}
                                />
                            </div>
                            {/* Violations window */}
                        </div>
                    </div>

                    <div
                        className={`${styles.main_settlement_container} flex-container`}
                    >
                        <div
                            className={`${styles.settlement_window_container} flex-container`}
                        >
                            <OutputLegend
                                color={getInstalmentColor(
                                    currenInstalmentPos?.status,
                                )}
                                content={
                                    <GetTicketInfoWindowContent
                                        installment={currenInstalmentPos}
                                        currency={currency}
                                    />
                                }
                                stretch={true}
                            />
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default PosTicketInfo;
